const DEFAULT_AUTH_URL = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
const DEFAULT_TOKEN_URL =
  "https://login.microsoftonline.com/ae6beacb-253c-4e6a-b814-e3d786f0e06e/oauth2/v2.0/token";
const DEFAULT_SCOPE =
  process.env.REACT_APP_MICROSOFT_SCOPE || "openid email profile";
const DEFAULT_REDIRECT_PATH = "/user/login";

const STORAGE_PREFIX = "isafe_microsoft";
const STATE_KEY = `${STORAGE_PREFIX}_state`;
const CODE_VERIFIER_KEY = `${STORAGE_PREFIX}_code_verifier`;

const base64UrlEncode = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const randomString = (length = 64) => {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => ("0" + byte.toString(16)).slice(-2)).join(
      ""
    );
  }
  return [...Array(length)]
    .map(() => Math.floor(Math.random() * 16).toString(16))
    .join("");
};

export const getMicrosoftClientId = () =>
  process.env.REACT_APP_MICROSOFT_CLIENT_ID || "";

export const getMicrosoftRedirectUri = () => {
  const envRedirect = process.env.REACT_APP_MICROSOFT_REDIRECT_URI;
  if (envRedirect) {
    return envRedirect;
  }
  if (typeof window !== "undefined" && window.location) {
    return `${window.location.origin}${DEFAULT_REDIRECT_PATH}`;
  }
  return DEFAULT_REDIRECT_PATH;
};

export const getMicrosoftScope = () => DEFAULT_SCOPE;

export const getMicrosoftAuthUrl = ({ state, codeChallenge }) => {
  const envAuth = process.env.REACT_APP_MICROSOFT_AUTH_URL;
  const authBase = (() => {
    if (envAuth && envAuth.toLowerCase().includes("/token")) {
      console.warn(
        "[MicrosoftSSO] REACT_APP_MICROSOFT_AUTH_URL points to a token endpoint; defaulting to the authorize endpoint."
      );
      return DEFAULT_AUTH_URL;
    }
    return envAuth || DEFAULT_AUTH_URL;
  })();
  const clientId = getMicrosoftClientId();
  const redirectUri = getMicrosoftRedirectUri();
  if (!clientId || !redirectUri || !codeChallenge || !state) {
    return "";
  }
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    redirect_uri: redirectUri,
    scope: getMicrosoftScope(),
    response_mode: "query",
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  return `${authBase}?${params.toString()}`;
};

export const getMicrosoftTokenUrl = () => {
  const envToken = process.env.REACT_APP_MICROSOFT_TOKEN_URL;
  if (envToken && envToken.toLowerCase().includes("/authorize")) {
    console.warn(
      "[MicrosoftSSO] REACT_APP_MICROSOFT_TOKEN_URL points to an authorize endpoint; defaulting to the token endpoint."
    );
    return DEFAULT_TOKEN_URL;
  }
  return envToken || DEFAULT_TOKEN_URL;
};

export const storeMicrosoftState = (state) => {
  try {
    window?.sessionStorage?.setItem(STATE_KEY, state);
  } catch (error) {
    console.warn("Unable to store Microsoft SSO state", error);
  }
};

export const readMicrosoftState = () => {
  try {
    return window?.sessionStorage?.getItem(STATE_KEY) || "";
  } catch (error) {
    console.warn("Unable to read Microsoft SSO state", error);
    return "";
  }
};

export const clearMicrosoftState = () => {
  try {
    window?.sessionStorage?.removeItem(STATE_KEY);
  } catch (error) {
    console.warn("Unable to clear Microsoft SSO state", error);
  }
};

export const storeMicrosoftCodeVerifier = (verifier) => {
  try {
    window?.sessionStorage?.setItem(CODE_VERIFIER_KEY, verifier);
  } catch (error) {
    console.warn("Unable to store Microsoft SSO code verifier", error);
  }
};

export const readMicrosoftCodeVerifier = () => {
  try {
    return window?.sessionStorage?.getItem(CODE_VERIFIER_KEY) || "";
  } catch (error) {
    console.warn("Unable to read Microsoft SSO code verifier", error);
    return "";
  }
};

export const clearMicrosoftCodeVerifier = () => {
  try {
    window?.sessionStorage?.removeItem(CODE_VERIFIER_KEY);
  } catch (error) {
    console.warn("Unable to clear Microsoft SSO code verifier", error);
  }
};

export const generateMicrosoftState = () => `msso_${randomString(24)}`;

export const generateCodeVerifier = () => {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
  }

  const fallback = randomString(64);
  const encoder = new TextEncoder();
  return base64UrlEncode(encoder.encode(fallback));
};

export const generateCodeChallenge = async (codeVerifier) => {
  if (typeof window === "undefined" || !window.crypto?.subtle) {
    throw new Error("missing_crypto");
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(digest);
};

export const prepareMicrosoftAuthRequest = async () => {
  const clientId = getMicrosoftClientId();
  const redirectUri = getMicrosoftRedirectUri();
  if (!clientId || !redirectUri) {
    throw new Error("missing_microsoft_config");
  }

  const state = generateMicrosoftState();
  const codeVerifier = generateCodeVerifier();
  storeMicrosoftState(state);
  storeMicrosoftCodeVerifier(codeVerifier);

  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const authUrl = getMicrosoftAuthUrl({ state, codeChallenge });
  if (!authUrl) {
    throw new Error("missing_auth_url");
  }

  console.info("[MicrosoftSSO] Preparing authorize request", {
    authUrlPreview: authUrl,
    clientId,
    redirectUri,
    scope: getMicrosoftScope(),
    state,
  });

  return {
    state,
    codeVerifier,
    codeChallenge,
    authUrl,
    redirectUri,
  };
};

export const parseMicrosoftIdToken = (idToken) => {
  try {
    const parts = idToken.split(".");
    if (parts.length < 2) {
      return {};
    }
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const payload = JSON.parse(atob(padded));
    return payload || {};
  } catch (error) {
    console.warn("Unable to parse Microsoft id_token", error);
    return {};
  }
};
